import { backendUrl, miMATIC } from '@jdiyield/constants'
import { LendContract, Lending, Position, PriceList, Token } from '@jdiyield/interfaces'
import { api } from '@jdiyield/utils'
import BigNumber from 'bignumber.js'
import throat from 'throat'
import Web3 from 'web3'

import { findToken, makeBatchRequest } from '../utils'

export async function getMaiFinanceVault(
  web3: Web3,
  walletAddress: string,
  lendContract: LendContract,
  tokens: Token[],
  priceList: PriceList
): Promise<Lending> {
  try {
    const tokenAddress = lendContract.lendTokens?.[0] || miMATIC
    const { QiStablecoin } = await import('@jdiyield/abi')
    const contract = new web3.eth.Contract(QiStablecoin as any, tokenAddress)
    const { data } = await api.get(`${backendUrl}/positions`, {
      params: {
        contract: tokenAddress,
        walletAddress
      }
    })
    const _VaultInfos = await makeBatchRequest(
      web3,
      data.flatMap(({ id }) => [contract.methods.vaultDebt(id).call, contract.methods.vaultCollateral(id).call])
    )
    const chunkVaultInfos = [...new Array(_VaultInfos.length / 2)].map((_, i) => {
      const index = i * 2
      return {
        debt: +_VaultInfos[index],
        collateral: +_VaultInfos[index + 1]
      }
    })
    const borrowTokenInfo = findToken(tokens, miMATIC)
    const collateralTokenInfo = findToken(tokens, '0x0000000000000000000000000000000000001010')

    const collateralAmount = new BigNumber(chunkVaultInfos.reduce((sum, { collateral }) => sum + collateral, 0))
    const debtAmount = new BigNumber(chunkVaultInfos.reduce((sum, { debt }) => sum + debt, 0))

    return {
      collateralTokens: [
        {
          logo: collateralTokenInfo?.logo || '',
          symbol: collateralTokenInfo?.symbol || '???',
          name: collateralTokenInfo?.name || '???',
          amount: collateralAmount,
          value: collateralAmount.dividedBy(`1e${collateralTokenInfo?.decimals || 18}`).toNumber(),
          valueUSD: collateralAmount
            .dividedBy(`1e${collateralTokenInfo?.decimals || 18}`)
            .multipliedBy(priceList['0x0000000000000000000000000000000000001010']?.usd || 0)
            .toNumber(),
          address: collateralTokenInfo?.address || miMATIC
        }
      ],
      supplyBalance: collateralAmount.dividedBy(`1e${borrowTokenInfo?.decimals || 18}`).toNumber(),
      borrowBalance: debtAmount.dividedBy(`1e${collateralTokenInfo?.decimals || 18}`).toNumber(),
      borrowTokens: [
        {
          logo: borrowTokenInfo?.logo || '',
          symbol: borrowTokenInfo?.symbol || '???',
          name: borrowTokenInfo?.name || '???',
          amount: debtAmount,
          value: debtAmount.dividedBy(`1e${borrowTokenInfo?.decimals || 18}`).toNumber(),
          valueUSD: debtAmount
            .dividedBy(`1e${borrowTokenInfo?.decimals || 18}`)
            .multipliedBy(priceList[miMATIC]?.usd || 0)
            .toNumber(),
          address: borrowTokenInfo?.address || miMATIC
        }
      ]
    } as Lending
  } catch (error) {
    console.log(error)
    return {
      collateralTokens: [],
      borrowBalance: 0,
      borrowTokens: [],
      supplyBalance: 0
    } as Lending
  }
}

export const getMaiPositions = async (web3: Web3, latestPosition = 0): Promise<Position[]> => {
  try {
    const { QiStablecoin } = await import('@jdiyield/abi')
    const ct = new web3.eth.Contract(QiStablecoin as any, miMATIC)
    const vaultCount = await ct.methods.vaultCount().call()

    if (latestPosition === +vaultCount) {
      return []
    }

    const mustFetchMore = [...new Array(+vaultCount - latestPosition)].map((_, i) => latestPosition + i + 1)
    if (mustFetchMore.length === 0) return []

    const chunkIndex = 1000
    const chunks = mustFetchMore.reduce((sum, id, index) => {
      if (sum.length === 0) {
        return [[id]]
      }
      if (index % chunkIndex === 0) {
        sum.push([id])
      } else {
        sum[sum.length - 1].push(id)
      }
      return sum
    }, [])
    const result = await Promise.all(
      chunks.flatMap(
        throat(1, async (arr) => {
          const rawPositionInfos = await makeBatchRequest(
            web3,
            arr.flatMap((id) => [ct.methods.vaultOwner(id).call, ct.methods.vaultCollateral(id).call])
          )
          const positionInfos = [...new Array(rawPositionInfos.length / 2)].map((_, i) => {
            const index = i * 2
            return {
              owner: rawPositionInfos[index],
              collateral: rawPositionInfos[index + 1]
            }
          })
          return positionInfos
            .map(({ owner, collateral }, index) =>
              +collateral === 0
                ? null
                : ({
                    owner,
                    id: arr[index]
                  } as Position)
            )
            .filter((obj) => !!obj)
        })
      )
    )
    return result.flat() as Position[]
  } catch (error) {
    console.log('MAI FINANCE', error)
    return []
  }
}
